import {gql} from '@apollo/client';
import {Model} from 'backbone';

import {BulkAssemblyStatusBarState} from 'components/StatusBar';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {User} from 'constants/UserConstants';
import {FileStateAttributes} from 'filestate/FileState';
import FolderItemState, {FolderItemStateAttributes} from 'filestate/FolderItemState';
import ApolloFolderItemBase, {FolderItemFileQueryVariables} from 'folder-items/ApolloFolderItemBase';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {graphQLArrayToREST} from 'graphql-client/declarative-translations/external/translate';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import QueryWrapper from 'graphql-client/QueryWrapper';
import {
  BulkAssemblyFileQuery,
  BulkAssemblyFileQueryVariables,
} from 'molbio/bulk-assembly/backbone/__generated/BulkAssemblyFileQuery';
import BulkAssemblyConstructsTabState from 'molbio/bulk-assembly/backbone/BulkAssemblyConstructsTabState';
import BulkAssemblyOverviewTabState from 'molbio/bulk-assembly/backbone/BulkAssemblyOverviewTabState';
import {
  BulkAssembly,
  CLIENT_PROCEDURE_JSON_CURRENT_VERSION,
} from 'molbio/bulk-assembly/BulkAssemblyConstants';
import * as logging from 'util/Logging';
import {Optional} from 'util/Types';

import {InaccessibleInfoGraphQL} from '../../../../graphql-fragments/auth/__generated/InaccessibleInfoGraphQL';

type BulkAssemblyNodeGraphQL = ExtractByTypename<BulkAssemblyFileQuery['bulkAssembly'], 'BulkAssemblyNode'>;

export const BULK_ASSEMBLY_FILE_QUERY = gql`
  query BulkAssemblyFileQuery($fileId: StrictString!, $procedureVersion: StrictString!)
  @engComponent(name: MOLBIO) {
    bulkAssembly(id: $fileId) @audit(msg: "Opened assembly {name}") {
      ... on BulkAssemblyNode {
        name
        isFinalized
        finalizedAt
        procedureJson: versionedProcedureJson(version: $procedureVersion)
        finalizedResultsJson
        ...BaseFolderItemFileGraphQL
      }
      ...HasAuthorsGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('BaseFolderItemFile').fragment}
  ${getFragmentWrapper('HasAuthors').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export class BulkAssemblyModel extends ApolloFolderItemBase<
  BulkAssemblyFileQuery,
  BulkAssemblyNodeGraphQL,
  BulkAssemblyFileQueryVariables
> {
  protected getQueryWrapper(): QueryWrapper<BulkAssemblyFileQuery, FolderItemFileQueryVariables> {
    return {query: BULK_ASSEMBLY_FILE_QUERY};
  }
  protected queryVariables(): BulkAssemblyFileQueryVariables {
    return {
      fileId: this.id,
      procedureVersion: CLIENT_PROCEDURE_JSON_CURRENT_VERSION,
    };
  }

  protected getGraphQLFolderItemFromQueryData(
    data: BulkAssemblyFileQuery
  ): BulkAssemblyNodeGraphQL | InaccessibleInfoGraphQL {
    return data.bulkAssembly;
  }

  protected getUpdatedQueryData(attrs: Partial<BulkAssemblyNodeGraphQL>): BulkAssemblyFileQuery {
    return {
      bulkAssembly: {
        ...this.folderItem(),
        ...attrs,
      },
    };
  }

  /**
   * Bulk Assembly models don't implement HasAuthorsMixin and there are no plans to implement it. This fake
   * authors field is populated with the assembly creator and used where authors are used presentationally.
   */
  override authors(): Array<User> {
    return graphQLArrayToREST('Author', this.folderItem().authors) as Array<User>;
  }

  override setAuthors(authors: Array<User>): void {
    logging.error('Unexpectedly attempted to set authors on a Bulk Assembly');
  }

  finalized(): Optional<{isFinalized: boolean; finalizedAt: Optional<string>}> {
    if (this.folderItem().isFinalized) {
      return {
        isFinalized: this.folderItem().isFinalized,
        finalizedAt: this.folderItem().finalizedAt,
      };
    } else {
      return null;
    }
  }

  override projectFolderId(): Optional<string> {
    return this.folderItem().folder.id;
  }

  getBulkAssembly(): BulkAssembly {
    return this.folderItem();
  }
}

interface BulkAssemblyFileStateAttrs {
  bulk_assembly: BulkAssemblyModel;
}

interface BulkAssemblyFileState {
  get<K extends keyof BulkAssemblyFileStateAttrs>(key: K): BulkAssemblyFileStateAttrs[K];
  set<K extends keyof BulkAssemblyFileStateAttrs>(
    attributeName: K,
    value: BulkAssemblyFileStateAttrs[K],
    options?: Backbone.ModelSetOptions
  ): BulkAssemblyFileState;
}

class BulkAssemblyFileState extends FolderItemState {
  static override Model: typeof BulkAssemblyModel;

  static override initClass(): void {
    this.Model = BulkAssemblyModel;
    this.fileType = ModelTypeConstants.BULK_ASSEMBLY;
    this.prototype.hasNote = false;
  }

  override hasStorage(): boolean {
    return false;
  }

  override hasMetadata(): boolean {
    return true;
  }

  override defaults(): FileStateAttributes {
    return {
      ...super.defaults(),
      toolbar: new Model(),
      statusbar: new BulkAssemblyStatusBarState(),
    };
  }

  override initialize(attributes: FolderItemStateAttributes, options: {}): void {
    super.initialize(attributes, options);
    const overviewTab = new BulkAssemblyOverviewTabState({bulkAssembly: this.get('bulk_assembly')});
    this.buffers().at(0).tabs().add(overviewTab);
    this.buffers()
      .at(0)
      .tabs()
      .add(new BulkAssemblyConstructsTabState({bulkAssembly: this.get('bulk_assembly')}, {}));
    if (this.metadataTab != null) {
      this.buffers().at(0).tabs().add(this.metadataTab);
    }
    overviewTab.set({active: true});
    this.setIsWorkspaceSplit(false);
  }
}

BulkAssemblyFileState.initClass();

export default BulkAssemblyFileState;
